// ===================================================================
// KAIXO - Pestaña Usuarios
// ===================================================================
// Búsqueda + filtro + tabla de usuarios. Click en uno abre panel de
// detalle con stats agregadas y acciones: banear, suspender, notas.
// ===================================================================

import React, { useEffect, useState, useCallback } from 'react';
import {
    adminUsersList, adminUsersGet,
    adminUserBan, adminUserUnban,
    adminUserSuspend, adminUserUnsuspend,
    adminUserNote,
    adminUserContactsList, adminUserContactsCleanupOrphans,
    adminContactDelete
} from '../../../services/adminApi';
import api from '../../../services/api';
import './AdminUsersSection.css';

const FILTERS = [
    { id: '',          label: 'Todos' },
    { id: 'paying',    label: 'De pago' },
    { id: 'verified',  label: 'Verificados' },
    { id: 'admin',     label: 'Admins' },
    { id: 'suspended', label: 'Suspendidos' },
    { id: 'banned',    label: 'Baneados' }
];

export default function AdminUsersSection() {
    const [list, setList] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [q, setQ] = useState('');
    const [filter, setFilter] = useState('');
    const [page, setPage] = useState(0);
    const [selected, setSelected] = useState(null);
    const PAGE_SIZE = 30;

    const reload = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const { data } = await adminUsersList({
                q, filter, limit: PAGE_SIZE, skip: page * PAGE_SIZE
            });
            setList(data?.users || []);
            setTotal(data?.total || 0);
        } catch (err) {
            setError(err.response?.data?.error || err.message);
        } finally {
            setLoading(false);
        }
    }, [q, filter, page]);

    useEffect(() => { reload(); }, [reload]);

    const openDetail = async (id) => {
        try {
            const { data } = await adminUsersGet(id);
            setSelected({ user: data.user, stats: data.stats, channels: data.channels || [] });
        } catch (err) {
            alert(err.response?.data?.error || err.message);
        }
    };

    return (
        <div className="aus-wrap">
            <div className="aus-toolbar">
                <input
                    className="aus-search"
                    placeholder="Buscar por nombre, email o teléfono…"
                    value={q}
                    onChange={e => { setQ(e.target.value); setPage(0); }}
                />
                <div className="aus-filters">
                    {FILTERS.map(f => (
                        <button
                            key={f.id}
                            className={`aus-filter ${filter === f.id ? 'is-active' : ''}`}
                            onClick={() => { setFilter(f.id); setPage(0); }}
                        >{f.label}</button>
                    ))}
                </div>
            </div>

            {loading && <div className="ap-loading">Cargando usuarios…</div>}
            {error && <div className="ap-error">{error}</div>}

            {!loading && (
                <>
                    <div className="aus-count">{total} usuarios</div>
                    <table className="aus-table">
                        <thead>
                            <tr>
                                <th>Usuario</th>
                                <th>Plan</th>
                                <th>Verificado</th>
                                <th>Estado</th>
                                <th>Registro</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(u => (
                                <tr key={u._id} onClick={() => openDetail(u._id)} className="aus-row">
                                    <td>
                                        <div className="aus-user-cell">
                                            <div className="aus-avatar">
                                                {u.avatar ? <img src={u.avatar} alt="" />
                                                          : <span>{(u.name || '?')[0].toUpperCase()}</span>}
                                            </div>
                                            <div>
                                                <strong>{u.name || '—'}</strong>
                                                <div className="aus-meta">{u.email || u.phone || '—'}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td>
                                        <span className={`aus-plan-chip aus-plan-${(u.planTier || 'free').toLowerCase()}`}>
                                            {u.planTier || 'FREE'}
                                        </span>
                                    </td>
                                    <td>{u.verifiedLevel ? <span className="aus-verif-chip">{u.verifiedLevel}</span> : '—'}</td>
                                    <td>
                                        {u.isBanned && <span className="aus-status aus-banned">BANEADO</span>}
                                        {!u.isBanned && u.suspendedUntil && new Date(u.suspendedUntil) > new Date() && (
                                            <span className="aus-status aus-suspended">SUSPENDIDO</span>
                                        )}
                                        {!u.isBanned && (!u.suspendedUntil || new Date(u.suspendedUntil) <= new Date()) && (
                                            <span className="aus-status aus-active">Activo</span>
                                        )}
                                        {u.isAdmin && <span className="aus-status aus-admin">ADMIN</span>}
                                    </td>
                                    <td className="aus-date">
                                        {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                                    </td>
                                    <td><button className="aus-btn-mini">Ver</button></td>
                                </tr>
                            ))}
                            {list.length === 0 && (
                                <tr><td colSpan={6} style={{ textAlign: 'center', padding: 24, color: '#888' }}>
                                    Sin resultados.
                                </td></tr>
                            )}
                        </tbody>
                    </table>

                    <div className="aus-pagination">
                        <button
                            className="aus-page-btn"
                            disabled={page === 0}
                            onClick={() => setPage(p => Math.max(0, p - 1))}
                        >← Anterior</button>
                        <span className="aus-page-info">
                            Página {page + 1} / {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                        </span>
                        <button
                            className="aus-page-btn"
                            disabled={(page + 1) * PAGE_SIZE >= total}
                            onClick={() => setPage(p => p + 1)}
                        >Siguiente →</button>
                    </div>
                </>
            )}

            {selected && (
                <UserDetailModal
                    user={selected.user}
                    stats={selected.stats}
                    channels={selected.channels}
                    onClose={() => setSelected(null)}
                    onChanged={() => { setSelected(null); reload(); }}
                />
            )}
        </div>
    );
}

function UserDetailModal({ user, stats, channels = [], onClose, onChanged }) {
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState(user.adminNote || '');
    const [contacts, setContacts] = useState(null);   // null = no cargado, [] = vacío
    const [contactsLoading, setContactsLoading] = useState(false);
    const [contactsFilter, setContactsFilter] = useState(''); // 'orphans' | ''

    const loadContacts = async () => {
        setContactsLoading(true);
        try {
            const res = await adminUserContactsList(user._id);
            setContacts(res.data?.contacts || []);
        } catch (e) {
            alert(e.response?.data?.error || e.message);
        } finally {
            setContactsLoading(false);
        }
    };

    const handleDeleteContact = async (c) => {
        if (!window.confirm(`Borrar de la agenda KAIXO el contacto "${c.contactName}" (${c.contactPhone})?`)) return;
        try {
            await adminContactDelete(c._id);
            setContacts(prev => (prev || []).filter(x => x._id !== c._id));
        } catch (e) { alert(e.response?.data?.error || e.message); }
    };

    const handleCleanupOrphans = async () => {
        if (!window.confirm('Limpiar todos los contactos huérfanos (marcados como Kaixo pero sin user vivo)? Pondrá hasKaixo=false en esas entradas, no las borrará.')) return;
        try {
            const res = await adminUserContactsCleanupOrphans(user._id);
            alert(`Huérfanos detectados: ${res.data.orphans} · actualizados: ${res.data.updated}`);
            await loadContacts();
        } catch (e) { alert(e.response?.data?.error || e.message); }
    };

    const wrap = async (fn) => {
        setBusy(true);
        try { await fn(); onChanged(); }
        catch (err) { alert(err.response?.data?.error || err.message); }
        finally { setBusy(false); }
    };

    const handleBan = () => {
        const reason = prompt('Motivo del baneo:');
        if (!reason) return;
        wrap(() => adminUserBan(user._id, reason));
    };
    const handleSuspend = () => {
        const daysRaw = prompt('¿Cuántos días suspender? (1-365)', '7');
        if (daysRaw === null) return;  // cancel
        const days = parseInt(daysRaw, 10);
        if (!Number.isFinite(days) || days < 1 || days > 365) {
            alert('Introduce un número entero entre 1 y 365.');
            return;
        }
        const reason = prompt('Motivo de la suspensión:');
        if (reason === null) return;  // cancel también aquí
        wrap(() => adminUserSuspend(user._id, days, reason || ''));
    };
    const saveNote = () => wrap(() => adminUserNote(user._id, note));

    const isSuspended = user.suspendedUntil && new Date(user.suspendedUntil) > new Date();

    return (
        <div className="aus-modal-overlay" onClick={onClose}>
            <div className="aus-modal" onClick={e => e.stopPropagation()}>
                <div className="aus-modal-head">
                    <div className="aus-user-cell">
                        <div className="aus-avatar aus-avatar-lg">
                            {user.avatar ? <img src={user.avatar} alt="" />
                                          : <span>{(user.name || '?')[0].toUpperCase()}</span>}
                        </div>
                        <div>
                            <h3>{user.name || '—'}</h3>
                            <div className="aus-uid">{user._id}</div>
                        </div>
                    </div>
                    <button className="aus-modal-close" onClick={onClose}>✕</button>
                </div>

                <div className="aus-modal-body">
                    <section className="aus-section">
                        <h4>Datos</h4>
                        <table className="aus-data-table">
                            <tbody>
                                <tr><th>Email</th><td>{user.email || '—'}</td></tr>
                                <tr><th>Teléfono</th><td>{user.phone || '—'}</td></tr>
                                <tr><th>País</th><td>{user.country || '—'}</td></tr>
                                <tr><th>Idioma</th><td>{user.language || '—'}</td></tr>
                                <tr><th>Tipo cuenta</th><td>{user.accountType || 'personal'}</td></tr>
                                <tr><th>Plan</th><td>{user.planTier || 'FREE'}{user.planExpiresAt && ` (hasta ${new Date(user.planExpiresAt).toLocaleDateString()})`}</td></tr>
                                <tr><th>Verificado</th><td>{user.verifiedLevel || 'No'}</td></tr>
                                <tr><th>Registro</th><td>{user.createdAt ? new Date(user.createdAt).toLocaleString() : '—'}</td></tr>
                                <tr><th>Última sesión</th><td>{user.lastSeen ? new Date(user.lastSeen).toLocaleString() : '—'}</td></tr>
                            </tbody>
                        </table>
                    </section>

                    <section className="aus-section">
                        <h4>Actividad</h4>
                        <div className="aus-stats-grid">
                            <div><div className="aus-stat-num">{stats?.messagesSent || 0}</div><div className="aus-stat-lbl">Mensajes enviados</div></div>
                            <div><div className="aus-stat-num">{stats?.calls || 0}</div><div className="aus-stat-lbl">Llamadas</div></div>
                        </div>
                    </section>

                    {stats?.lastSubscription && (
                        <section className="aus-section">
                            <h4>Última suscripción</h4>
                            {/* Mapping legacy: docs históricos de la collection `subscriptions` antes de la
                                migración a STARTER/GROWTH/PRO/SCALE. El script `migrate-old-subscriptions.js`
                                limpia los datos pero hasta entonces el panel admin marca el origen. */}
                            <div>{(() => {
                                const legacyMap = { premium: 'STARTER (legacy)', business: 'GROWTH (legacy)', enterprise: 'PRO (legacy)', free: 'FREE (legacy)' };
                                const label = legacyMap[stats.lastSubscription.plan] || stats.lastSubscription.plan;
                                return `${label} · ${stats.lastSubscription.status} · $${(stats.lastSubscription.priceCents / 100).toFixed(2)} ${stats.lastSubscription.currency}`;
                            })()}</div>
                        </section>
                    )}

                    {(user.isBanned || isSuspended) && (
                        <section className="aus-section aus-section-warn">
                            <h4>Estado restringido</h4>
                            {user.isBanned && (
                                <div>
                                    <strong>BANEADO</strong> desde {new Date(user.bannedAt).toLocaleDateString()}
                                    {user.banReason && <p>Motivo: {user.banReason}</p>}
                                </div>
                            )}
                            {isSuspended && (
                                <div>
                                    <strong>SUSPENDIDO</strong> hasta {new Date(user.suspendedUntil).toLocaleDateString()}
                                </div>
                            )}
                        </section>
                    )}

                    <section className="aus-section">
                        <h4>Nota interna</h4>
                        <textarea
                            className="aus-note"
                            rows={3}
                            value={note}
                            onChange={e => setNote(e.target.value)}
                            placeholder="Visible solo para admins…"
                        />
                        <button className="aus-btn-secondary" onClick={saveNote} disabled={busy}>
                            Guardar nota
                        </button>
                    </section>

                    <div className="aus-actions">
                        {user.isBanned ? (
                            <button className="aus-btn-success" onClick={() => wrap(() => adminUserUnban(user._id))} disabled={busy}>
                                Levantar baneo
                            </button>
                        ) : (
                            <button className="aus-btn-danger" onClick={handleBan} disabled={busy}>
                                Banear
                            </button>
                        )}
                        {isSuspended ? (
                            <button className="aus-btn-success" onClick={() => wrap(() => adminUserUnsuspend(user._id))} disabled={busy}>
                                Levantar suspensión
                            </button>
                        ) : (
                            <button className="aus-btn-warn" onClick={handleSuspend} disabled={busy}>
                                Suspender N días
                            </button>
                        )}
                        {user.verifiedLevel && (
                            <button
                                className="aus-btn-warn"
                                onClick={async () => {
                                    const reason = prompt('Motivo para quitar la verificación:', 'Mal uso reportado');
                                    if (reason == null) return;
                                    if (!window.confirm(`¿Quitar verificación ${user.verifiedLevel} a ${user.name}?`)) return;
                                    try {
                                        await api.post(`/admin/users/${user._id}/remove-verification`, { reason });
                                        alert('Verificación eliminada');
                                        wrap(() => adminUsersGet(user._id));
                                    } catch (e) { alert(e?.response?.data?.error || e.message); }
                                }}
                                disabled={busy}
                            >
                                Quitar verificación
                            </button>
                        )}
                        <button
                            className="aus-btn-danger"
                            onClick={async () => {
                                const reason = prompt('Motivo de eliminación (queda en auditoría):',
                                    'Eliminación por mal uso: contenido ilegal / discurso de odio / spam');
                                if (reason == null) return;
                                if (!window.confirm(`⚠ ELIMINAR cuenta de ${user.name} permanentemente. Esta acción NO se puede deshacer fácilmente. ¿Continuar?`)) return;
                                if (!window.confirm('Doble confirmación: ¿estás SEGURO?')) return;
                                try {
                                    await api.post(`/admin/users/${user._id}/delete-for-misuse`, { reason });
                                    alert('Cuenta eliminada');
                                    wrap(() => adminUsersGet(user._id));
                                } catch (e) { alert(e?.response?.data?.error || e.message); }
                            }}
                            disabled={busy}
                            style={{ background: '#7f1d1d' }}
                        >
                            Eliminar cuenta (mal uso)
                        </button>
                    </div>

                    {/* Agenda KAIXO del usuario — diagnóstico de contactos
                        "fantasma" (hasKaixo:true sin User vivo detrás) y
                        eliminación puntual de entradas. */}
                    <section className="aus-section" style={{ marginTop: 18, borderTop: '1px solid #eee', paddingTop: 14 }}>
                        <h4>Agenda KAIXO de este usuario</h4>
                        {contacts === null ? (
                            <div>
                                <button className="aus-btn-secondary" onClick={loadContacts} disabled={contactsLoading}>
                                    {contactsLoading ? 'Cargando…' : 'Cargar agenda'}
                                </button>
                                <p style={{ fontSize: 12, color: '#888', marginTop: 6 }}>
                                    Lista de contactos guardados en la BD desde la sincronización del móvil.
                                    Útil para detectar entradas marcadas como "tienen Kaixo" pero cuyo usuario ya no existe.
                                </p>
                            </div>
                        ) : (
                            <>
                                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 13, color: '#666' }}>
                                        {contacts.length} contactos · huérfanos: <strong style={{ color: '#b91c1c' }}>{contacts.filter(c => c.isOrphan).length}</strong>
                                    </span>
                                    <button className="aus-btn-secondary" onClick={() => setContactsFilter(contactsFilter === 'orphans' ? '' : 'orphans')}>
                                        {contactsFilter === 'orphans' ? 'Mostrar todos' : 'Solo huérfanos'}
                                    </button>
                                    <button className="aus-btn-warn" onClick={handleCleanupOrphans} disabled={busy}>
                                        Limpiar huérfanos
                                    </button>
                                    <button className="aus-btn-secondary" onClick={loadContacts}>↻</button>
                                </div>
                                <div style={{ maxHeight: 280, overflowY: 'auto', border: '1px solid #eee', borderRadius: 6 }}>
                                    <table className="aus-data-table" style={{ width: '100%', fontSize: 13 }}>
                                        <thead>
                                            <tr style={{ background: '#fafafa' }}>
                                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Nombre</th>
                                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Teléfono</th>
                                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>Kaixo</th>
                                                <th style={{ textAlign: 'left', padding: '6px 8px' }}>User vinculado</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(contactsFilter === 'orphans' ? contacts.filter(c => c.isOrphan) : contacts).map(c => (
                                                <tr key={c._id} style={c.isOrphan ? { background: '#fef2f2' } : undefined}>
                                                    <td style={{ padding: '6px 8px' }}>{c.contactName}</td>
                                                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{c.contactPhone}</td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        {c.hasKaixo
                                                            ? (c.isOrphan
                                                                ? <span style={{ color: '#b91c1c', fontWeight: 600 }}>FANTASMA</span>
                                                                : <span style={{ color: '#15803d' }}>sí</span>)
                                                            : <span style={{ color: '#888' }}>no</span>}
                                                    </td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        {c.linkedUserName ? `${c.linkedUserName} (${c.linkedUserPhone || '—'})` : (c.contactUserId ? <em style={{ color: '#888' }}>id sin user vivo</em> : '—')}
                                                    </td>
                                                    <td style={{ padding: '6px 8px' }}>
                                                        <button className="aus-btn-danger" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => handleDeleteContact(c)}>
                                                            Borrar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                            {contacts.length === 0 && (
                                                <tr><td colSpan={5} style={{ padding: 16, color: '#888', textAlign: 'center' }}>Sin contactos sincronizados</td></tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                    </section>

                    {/* Programa Partner: otorgar verificación bypass + contrato.
                        IMPORTANTE: la verificación se aplica a un CANAL del usuario,
                        no al usuario en sí (los badges viven a nivel de canal). */}
                    <section className="aus-section" style={{ marginTop: 18, borderTop: '1px solid #eee', paddingTop: 14 }}>
                        <h4>Programa Partner / Founding Creator</h4>
                        {user.partnerProgram?.tier ? (
                            <>
                                <p style={{ fontSize: 13, color: '#666', margin: '4px 0' }}>
                                    Tier: <strong>{user.partnerProgram.tier}</strong>
                                    {user.partnerProgram.channelId && (() => {
                                        const ch = channels.find(c => String(c._id) === String(user.partnerProgram.channelId));
                                        return <> · Canal: <strong>{ch?.name || user.partnerProgram.channelId}</strong></>;
                                    })()}
                                    · Hasta: {user.partnerProgram.endDate ? new Date(user.partnerProgram.endDate).toLocaleDateString('es-ES') : '—'}
                                </p>
                                <button className="aus-btn-danger" onClick={async () => {
                                    if (!window.confirm('¿Revocar verificación de partner? El canal pierde la insignia.')) return;
                                    try {
                                        await api.post('/admin/verifications/revoke-partner', {
                                            userId: user._id,
                                            channelId: user.partnerProgram.channelId || undefined
                                        });
                                        alert('Revocado');
                                        wrap(() => adminUsersGet(user._id));
                                    } catch (e) { alert(e?.response?.data?.error || e.message); }
                                }} disabled={busy}>
                                    Revocar partner
                                </button>
                            </>
                        ) : (
                            <PartnerGrantForm
                                userId={user._id}
                                userName={user.name}
                                channels={channels}
                                onDone={() => wrap(() => adminUsersGet(user._id))}
                            />
                        )}
                    </section>
                </div>
            </div>
        </div>
    );
}

// ─── Form para otorgar verificación de partner (admin_general) ───
// La verificación se aplica a un CANAL del usuario, no al usuario.
function PartnerGrantForm({ userId, userName, channels = [], onDone }) {
    const [channelId, setChannelId] = useState(channels[0]?._id || '');
    const [level, setLevel] = useState('creator');
    const [tier, setTier] = useState('founding_creator');
    const [contractMonths, setContractMonths] = useState(24);
    const [notes, setNotes] = useState('');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    const submit = async () => {
        if (!channelId) { setErr('Debes elegir un canal'); return; }
        const ch = channels.find(c => String(c._id) === String(channelId));
        if (!window.confirm(`Otorgar verificación ${level} al canal "${ch?.name}" de ${userName} por ${contractMonths} meses (programa ${tier})?`)) return;
        setBusy(true);
        setErr('');
        try {
            await api.post('/admin/verifications/grant-partner', {
                userId, channelId, level, tier, contractMonths, notes
            });
            onDone && onDone();
        } catch (e) {
            setErr(e?.response?.data?.error || e.message);
        } finally {
            setBusy(false);
        }
    };

    return (
        <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
            <p style={{ fontSize: 12, color: '#888', margin: 0, lineHeight: 1.5 }}>
                Otorga verificación a un CANAL del usuario sin pasar por los requisitos
                normales. La insignia va al canal, no al perfil.
            </p>

            {/* Lista de canales con radio (uno solo verificable a la vez) */}
            <div>
                <strong style={{ fontSize: 13, display: 'block', marginBottom: 6 }}>
                    Canal a verificar:
                </strong>
                {channels.length === 0 ? (
                    <p style={{ fontSize: 12, color: '#c0392b', margin: '4px 0' }}>
                        Este usuario no tiene canales propios. Pídele que cree primero el canal
                        que va a verificarse.
                    </p>
                ) : (
                    <div style={{ display: 'grid', gap: 4, border: '1px solid #eee', borderRadius: 8, padding: 6, background: '#fafafa' }}>
                        {channels.map(c => {
                            const isChecked = String(channelId) === String(c._id);
                            return (
                                <label
                                    key={c._id}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 10,
                                        padding: '6px 8px',
                                        borderRadius: 6,
                                        background: isChecked ? '#fff' : 'transparent',
                                        border: isChecked ? '1px solid #6366f1' : '1px solid transparent',
                                        cursor: busy ? 'not-allowed' : 'pointer',
                                        fontSize: 13
                                    }}
                                >
                                    <input
                                        type="radio"
                                        name="partner-channel"
                                        value={c._id}
                                        checked={isChecked}
                                        onChange={() => setChannelId(c._id)}
                                        disabled={busy}
                                    />
                                    {c.avatar
                                        ? <img src={c.avatar} alt="" style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover' }} />
                                        : <span style={{
                                            width: 28, height: 28, borderRadius: '50%',
                                            background: '#ddd', display: 'inline-flex',
                                            alignItems: 'center', justifyContent: 'center', fontSize: 12
                                        }}>{(c.name || '?')[0].toUpperCase()}</span>}
                                    <span style={{ flex: 1 }}>
                                        <strong>{c.name}</strong>
                                        <span style={{ fontSize: 11, color: '#888', marginLeft: 6 }}>
                                            ({c.type || 'personal'})
                                        </span>
                                    </span>
                                    {c.verifiedLevel && (
                                        <span style={{
                                            fontSize: 11, padding: '2px 8px', borderRadius: 10,
                                            background: '#dcfce7', color: '#166534'
                                        }}>
                                            ya {c.verifiedLevel}
                                        </span>
                                    )}
                                </label>
                            );
                        })}
                    </div>
                )}
            </div>

            <label style={{ fontSize: 12 }}>
                Nivel:
                <select value={level} onChange={e => setLevel(e.target.value)} style={{ marginLeft: 8 }}>
                    <option value="basic">Básica</option>
                    <option value="creator">Creator</option>
                    <option value="business">Empresa</option>
                </select>
            </label>
            <label style={{ fontSize: 12 }}>
                Tier:
                <select value={tier} onChange={e => setTier(e.target.value)} style={{ marginLeft: 8 }}>
                    <option value="founding_creator">Founding Creator</option>
                    <option value="partner_business">Partner Business</option>
                    <option value="ambassador">Embajador</option>
                </select>
            </label>
            <label style={{ fontSize: 12 }}>
                Contrato (meses):
                <input type="number" value={contractMonths} onChange={e => setContractMonths(parseInt(e.target.value) || 24)}
                    min={3} max={60} style={{ marginLeft: 8, width: 70 }} />
            </label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
                placeholder="Notas internas (compromiso, contacto, etc.)"
                rows={2} maxLength={1000}
                style={{ fontSize: 12, padding: 6, borderRadius: 6, border: '1px solid #ccc' }} />
            {err && <div style={{ color: '#c0392b', fontSize: 12 }}>{err}</div>}
            <button
                className="aus-btn-primary"
                onClick={submit}
                disabled={busy || channels.length === 0 || !channelId}
                title={!channelId ? 'Selecciona un canal' : ''}
            >
                {busy ? 'Otorgando...' : 'Otorgar verificación de partner'}
            </button>
        </div>
    );
}
